import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, X, RefreshCw, ListChecks, Lock } from 'lucide-react';
import { assessmentService, companyService, questionService, templateService } from '../services';
import Layout from '../components/Layout';
import { Card, Badge, Button, ProgressBar, Toast, Sheet } from '../components/ds';
import { useActiveAudit } from '../contexts/ActiveAuditContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import {
  deriveStatus, ORIGINAL_META, getPriority, calcProgress, getEffectiveProgress,
  selectControls, UMSETZUNG_STATUSES, UMSETZUNG_META, getUmsetzungMeta,
} from '../utils/posture';

const FONT      = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const BRAND     = '#1E4FBE';

const PRIORITIES = [
  { value: 'hoch',     label: 'Hoch',     tone: 'critical' },
  { value: 'mittel',   label: 'Mittel',   tone: 'warning' },
  { value: 'niedrig',  label: 'Niedrig',  tone: 'neutral' },
];

const STATUS_FILTERS = UMSETZUNG_STATUSES.map(s => ({ value: s, ...UMSETZUNG_META[s] }));
const OPEN_PROGRESS_STATUSES = ['offen', 'in_umsetzung'];

const formatDate = (d) => new Date(d).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });

// ── Toggle-Chip (Filter-Facette) ────────────────────────────────────────
const FilterChip = ({ active, tone, label, onClick }) => (
  <button onClick={onClick} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          aria-pressed={active}>
    <Badge tone={active ? tone : 'neutral'} variant={active ? 'solid' : 'soft'}>{label}</Badge>
  </button>
);

// ── Umsetzungsstatus-Auswahl (Inline & Detail) ──────────────────────────
const ProgressSelect = ({ value, onChange, disabled }) => (
  <select
    value={value || 'offen'}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    aria-label="Umsetzungsfortschritt ändern"
    style={{
      padding: '8px 10px', minHeight: 40, borderRadius: 6, fontSize: 12.5, fontFamily: FONT,
      border: '1px solid #D7DCE4', background: '#fff', color: '#101623',
      cursor: disabled ? 'wait' : 'pointer',
    }}
  >
    {UMSETZUNG_STATUSES.map(s => (
      <option key={s} value={s}>{UMSETZUNG_META[s].label}</option>
    ))}
  </select>
);

// ── Detail-Drawer Inhalt (geteilt zwischen Desktop-Panel & Mobile-Sheet) ──
const ControlDrawerBody = ({ question, record, originalStatus, onProgressChange, saving }) => {
  const progressStatus = getEffectiveProgress(question, record);
  const progressMeta = getUmsetzungMeta(progressStatus);
  const original = originalStatus ? ORIGINAL_META[originalStatus] : null;
  const priority = getPriority(question);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Badge tone="info">{question.category}</Badge>
        <Badge tone={priority.tone}>{priority.label}</Badge>
        {original && <Badge tone={original.tone}>{original.label}</Badge>}
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#8A93A3', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Umsetzungsfortschritt</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Badge tone={progressMeta.tone}>{progressMeta.label}</Badge>
          <ProgressSelect value={progressStatus} onChange={onProgressChange} disabled={saving} />
        </div>
      </div>

      {question.anforderung && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#8A93A3', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Anforderung</div>
          <p style={{ margin: 0, fontSize: 13.5, color: '#2C3646', lineHeight: 1.6 }}>{question.anforderung}</p>
        </div>
      )}

      {record?.notes && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#8A93A3', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Befund</div>
          <p style={{ margin: 0, fontSize: 13.5, color: '#2C3646', lineHeight: 1.6 }}>{record.notes}</p>
        </div>
      )}

      {question.handlungsempfehlung && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#8A93A3', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Empfehlung</div>
          <p style={{ margin: 0, fontSize: 13.5, color: '#2C3646', lineHeight: 1.6 }}>{question.handlungsempfehlung}</p>
        </div>
      )}
    </div>
  );
};

// ── Detail-Drawer ─ docked panel (desktop) oder Vollbild-Sheet (mobil) ──
const ControlDrawer = ({ question, record, originalStatus, onClose, onProgressChange, saving }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet open onClose={onClose} variant="full" title={question.text}>
        {question.dinNr && (
          <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: '#8A93A3', marginBottom: 12 }}>Kontrolle {question.dinNr}</div>
        )}
        <ControlDrawerBody question={question} record={record} originalStatus={originalStatus} onProgressChange={onProgressChange} saving={saving} />
      </Sheet>
    );
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(10,14,23,0.45)', backdropFilter: 'blur(2px)' }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 460, maxWidth: '92vw',
        background: '#fff', zIndex: 910, boxShadow: '-12px 0 28px rgba(16,22,35,0.12)',
        display: 'flex', flexDirection: 'column', fontFamily: FONT,
      }}>
        <div style={{ padding: '22px 24px', borderBottom: '1px solid #EDEFF3', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            {question.dinNr && <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: '#8A93A3', marginBottom: 4 }}>Kontrolle {question.dinNr}</div>}
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#101623', lineHeight: 1.4 }}>{question.text}</h2>
          </div>
          <button onClick={onClose} aria-label="Schließen" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8A93A3', flexShrink: 0 }}>
            <X width={20} height={20} />
          </button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          <ControlDrawerBody question={question} record={record} originalStatus={originalStatus} onProgressChange={onProgressChange} saving={saving} />
        </div>
      </div>
    </>
  );
};

// ── Filter-Facetten (geteilt zwischen Desktop-Inline & Mobile-Sheet) ────
const FacetRows = ({ categories, selectedCategories, toggleCategory, selectedStatuses, toggleStatus, selectedPriorities, togglePriority }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 11, color: '#8A93A3', width: 68 }}>Bereich</span>
      {categories.map(c => (
        <FilterChip key={c} active={selectedCategories.has(c)} tone="info" label={c} onClick={() => toggleCategory(c)} />
      ))}
    </div>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 11, color: '#8A93A3', width: 68 }}>Status</span>
      {STATUS_FILTERS.map(s => (
        <FilterChip key={s.value} active={selectedStatuses.has(s.value)} tone={s.tone} label={s.label} onClick={() => toggleStatus(s.value)} />
      ))}
    </div>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 11, color: '#8A93A3', width: 68 }}>Priorität</span>
      {PRIORITIES.map(p => (
        <FilterChip key={p.value} active={selectedPriorities.has(p.value)} tone={p.tone} label={p.label} onClick={() => togglePriority(p.value)} />
      ))}
    </div>
  </div>
);

// ── Kontroll-Zeile als Karte (mobil, ersetzt die Tabellenzeile) ─────────
const ControlRowCard = ({ question, record, originalStatus, progressStatus, priority, saving, onOpen, onProgressChange }) => {
  const original = originalStatus ? ORIGINAL_META[originalStatus] : null;
  return (
    <div
      onClick={onOpen}
      style={{ padding: '14px 16px', borderBottom: '1px solid #F2F3F5', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: '#8A93A3' }}>{question.dinNr || '–'}</span>
        <Badge tone={priority.tone}>{priority.label}</Badge>
      </div>
      <div style={{ fontSize: 13.5, color: '#101623', lineHeight: 1.4 }}>{question.text}</div>
      {record?.notes && <div style={{ fontSize: 12, color: '#8A93A3' }}>{record.notes}</div>}
      {original && <Badge tone={original.tone}>{original.label}</Badge>}
      <div onClick={e => e.stopPropagation()}>
        <ProgressSelect value={progressStatus} disabled={saving} onChange={onProgressChange} />
      </div>
    </div>
  );
};

const ControlsPage = () => {
  const { auditId, controlId } = useParams();
  const navigate = useNavigate();
  const { setActiveAuditId } = useActiveAudit();
  const isMobile = useIsMobile();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const [company,     setCompany]     = useState(null);
  const [assessment,  setAssessment]  = useState(null);
  const [questions,   setQuestions]   = useState([]);
  const [answersMap,  setAnswersMap]  = useState({}); // questionId → answer record
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [savingIds,   setSavingIds]   = useState(() => new Set());
  const [toast,       setToast]       = useState(null);

  // Filter-State
  const [search,             setSearch]             = useState('');
  const [selectedCategories, setSelectedCategories] = useState(() => new Set());
  const [selectedStatuses,   setSelectedStatuses]   = useState(() => new Set(OPEN_PROGRESS_STATUSES));
  const [selectedPriorities, setSelectedPriorities] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const aRes = await assessmentService.getAssessment(auditId);
      setAssessment(aRes.data);
      setActiveAuditId(auditId);

      const [cRes, qRes, ansRes, tRes] = await Promise.all([
        companyService.getMyCompany(),
        questionService.getAllQuestions(aRes.data.templateId),
        questionService.getAnswersByAssessment(auditId),
        templateService.getTemplates(),
      ]);
      setCompany(cRes.data);
      setQuestions(qRes.data);

      const map = {};
      ansRes.data.forEach(a => { map[a.questionId] = a; });
      setAnswersMap(map);

      const tpl = tRes.data.find(t => t.id === aRes.data.templateId);
      setCategoryOrder(tpl?.categories || []);
    } catch (err) {
      if (err.response?.status === 404) {
        navigate('/audits', { replace: true, state: { notice: 'Dieses Audit existiert nicht mehr.' } });
        return;
      }
      setError('Kontrollen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [auditId, navigate, setActiveAuditId]);

  useEffect(() => { load(); }, [load]);

  const isCompleted = assessment?.status === 'completed';

  // Ebene 1 (fixiert) und Ebene 2 (Fortschritt) — komplett getrennt berechnet.
  const frozenPosture = assessment?.frozenPosture || null;
  const progress = useMemo(() => calcProgress(questions, answersMap), [questions, answersMap]);

  // Kontrollen = relevante Anforderungen des Audits (selectControls): Statusabfragen
  // und per Statusabfrage irrelevante Zweige sind keine Maßnahmen — die Liste muss
  // exakt dem entsprechen, was im Audit-Ergebnis/Report gewertet wurde.
  const rows = useMemo(() => selectControls(questions, answersMap).map(q => {
    const record = answersMap[q.id];
    return {
      question: q,
      record,
      originalStatus: deriveStatus(q, record),
      progressStatus: getEffectiveProgress(q, record),
      priority: getPriority(q),
    };
  }), [questions, answersMap]);

  const categories = useMemo(() => {
    const present = [...new Set(rows.map(r => r.question.category))];
    const ordered = categoryOrder.filter(c => present.includes(c));
    const rest = present.filter(c => !ordered.includes(c));
    return [...ordered, ...rest];
  }, [rows, categoryOrder]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(({ question, record, progressStatus }) => {
      if (selectedCategories.size > 0 && !selectedCategories.has(question.category)) return false;
      if (selectedStatuses.size > 0 && !selectedStatuses.has(progressStatus)) return false;
      if (selectedPriorities.size > 0 && !selectedPriorities.has(getPriority(question).value)) return false;
      if (term) {
        const haystack = `${question.dinNr || ''} ${question.text} ${record?.notes || ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [rows, selectedCategories, selectedStatuses, selectedPriorities, search]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(row => {
      if (!map[row.question.category]) map[row.question.category] = [];
      map[row.question.category].push(row);
    });
    return map;
  }, [filtered]);

  const toggleInSet = (setter) => (value) => setter(prev => {
    const next = new Set(prev);
    if (next.has(value)) next.delete(value); else next.add(value);
    return next;
  });

  const toggleCategory = toggleInSet(setSelectedCategories);
  const togglePriority = toggleInSet(setSelectedPriorities);
  const toggleStatus   = toggleInSet(setSelectedStatuses);

  const applyOpenPreset = () => {
    const isPreset = OPEN_PROGRESS_STATUSES.length === selectedStatuses.size
      && OPEN_PROGRESS_STATUSES.every(s => selectedStatuses.has(s));
    setSelectedStatuses(isPreset ? new Set() : new Set(OPEN_PROGRESS_STATUSES));
  };
  const isOpenPresetActive = OPEN_PROGRESS_STATUSES.length === selectedStatuses.size
    && OPEN_PROGRESS_STATUSES.every(s => selectedStatuses.has(s));

  const clearFilters = () => {
    setSearch('');
    setSelectedCategories(new Set());
    setSelectedStatuses(new Set());
    setSelectedPriorities(new Set());
  };
  const hasActiveFilters = Boolean(search.trim() || selectedCategories.size || selectedStatuses.size || selectedPriorities.size);

  const handleProgressChange = async (questionId, newStatus) => {
    const previous = answersMap[questionId];
    setSavingIds(prev => new Set(prev).add(questionId));
    // Optimistic update — betrifft ausschließlich umsetzungsStatus.
    setAnswersMap(prev => ({ ...prev, [questionId]: { ...(prev[questionId] || {}), questionId, assessmentId: auditId, umsetzungsStatus: newStatus } }));
    try {
      const res = await questionService.updateUmsetzungsStatus({ assessmentId: auditId, questionId, umsetzungsStatus: newStatus });
      setAnswersMap(prev => ({ ...prev, [questionId]: res.data }));
    } catch (err) {
      setAnswersMap(prev => ({ ...prev, [questionId]: previous }));
      setToast('Status konnte nicht gespeichert werden. Bitte erneut versuchen.');
    } finally {
      setSavingIds(prev => { const next = new Set(prev); next.delete(questionId); return next; });
    }
  };

  // Deep-Link nur auf echte Kontrollen (rows), nicht auf Statusabfragen/irrelevante Fragen.
  const openControl = rows.find(r => r.question.id === controlId)?.question || null;
  const openRecord = openControl ? answersMap[openControl.id] : null;

  // ── Loading ──
  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', color: '#8A93A3', fontSize: 14, fontFamily: FONT }}>
        Lädt…
      </div>
    </Layout>
  );

  // ── Error ──
  if (error) return (
    <Layout>
      <div style={{ padding: '36px 40px', fontFamily: FONT }}>
        <Card padding={0}>
          <div style={{ padding: '48px 40px', textAlign: 'center' }}>
            <p style={{ margin: '0 0 18px', fontSize: 14, color: '#C22B31' }}>{error}</p>
            <Button size="md" onClick={load} leadingIcon={<RefreshCw width={14} height={14} />}>
              Erneut versuchen
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );

  // ── Audit noch nicht abgeschlossen: Kontrollen sind noch nicht sichtbar ──
  if (!isCompleted) return (
    <Layout>
      <div style={{ padding: '36px 40px', fontFamily: FONT }}>
        <h1 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700, color: '#101623', letterSpacing: '-0.011em' }}>
          Kontrollen
        </h1>
        <Card padding={0}>
          <div style={{ padding: '64px 40px', textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16, background: '#F2F6FE', color: BRAND,
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
            }}>
              <Lock width={28} height={28} />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#101623' }}>
              Kontrollen erscheinen nach Abschluss des Audits
            </h2>
            <p style={{ color: '#667085', fontSize: 14, margin: '0 0 24px', lineHeight: 1.7, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
              Die Maßnahmenliste zu diesem Audit wird freigeschaltet, sobald die Erhebung abgeschlossen ist und das Ergebnis feststeht.
            </p>
            <Button size="md" onClick={() => navigate(`/assessment/${auditId}`)}>Zum Audit →</Button>
          </div>
        </Card>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: '36px 40px 48px', fontFamily: FONT }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#101623', letterSpacing: '-0.011em' }}>
              Kontrollen
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8A93A3', fontFamily: FONT_MONO }}>
              {company?.name} · {formatDate(assessment.createdAt)}
            </p>
          </div>
          {frozenPosture && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8A93A3', marginBottom: 2 }}>Sicherheitsstatus (fixiert)</div>
              <div style={{ fontFamily: FONT_MONO, fontSize: 20, fontWeight: 700, color: '#101623' }}>{frozenPosture.overallScore}<span style={{ fontSize: 12, color: '#8A93A3' }}>/100</span></div>
              <div style={{ fontSize: 11, color: '#8A93A3' }}>{frozenPosture.totalFindings} offene Befunde im Ergebnis</div>
            </div>
          )}
        </div>

        {/* ── Umsetzungsfortschritt: eigener, klar getrennter Indikator ── */}
        <Card padding={16} style={{ marginBottom: 16, background: '#FAFBFC' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#101623' }}>Maßnahmenfortschritt</span>
            <span style={{ fontSize: 12, color: '#667085', fontFamily: FONT_MONO }}>{progress.done} von {progress.total} Maßnahmen erledigt</span>
          </div>
          <ProgressBar value={progress.percent} tone="brand" />
        </Card>

        {/* ── Filterleiste ── */}
        <Card padding={16} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isMobile ? 0 : 14, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: isMobile ? undefined : 360, minWidth: 160 }}>
              <Search width={15} height={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#8A93A3' }} />
              <input
                type="text" placeholder="Suche nach Titel, ID oder Befund…"
                value={search} onChange={e => setSearch(e.target.value)}
                aria-label="Kontrollen durchsuchen"
                style={{
                  width: '100%', padding: '8px 10px 8px 32px', boxSizing: 'border-box',
                  border: '1px solid #D7DCE4', borderRadius: 8, fontSize: 13, fontFamily: FONT, outline: 'none',
                }}
              />
            </div>
            {!isMobile && <FilterChip active={isOpenPresetActive} tone="critical" label="Nur offene Maßnahmen" onClick={applyOpenPreset} />}
            <span style={{ fontSize: 12, color: '#8A93A3', marginLeft: isMobile ? 0 : 'auto' }}>{filtered.length} Kontrollen</span>
            {isMobile && (
              <button
                onClick={() => setFilterSheetOpen(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0 14px',
                  border: '1px solid #D7DCE4', borderRadius: 8, background: hasActiveFilters ? '#F2F6FE' : '#fff',
                  color: hasActiveFilters ? '#1B4098' : '#2C3646', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: FONT,
                }}
              >
                Filter{hasActiveFilters ? ' •' : ''}
              </button>
            )}
            {!isMobile && hasActiveFilters && (
              <button onClick={clearFilters} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#667085', fontSize: 12, fontFamily: FONT }}>
                <X width={12} height={12} /> Filter zurücksetzen
              </button>
            )}
          </div>

          {!isMobile && (
            <FacetRows
              categories={categories}
              selectedCategories={selectedCategories} toggleCategory={toggleCategory}
              selectedStatuses={selectedStatuses} toggleStatus={toggleStatus}
              selectedPriorities={selectedPriorities} togglePriority={togglePriority}
            />
          )}
        </Card>

        {isMobile && (
          <Sheet open={filterSheetOpen} onClose={() => setFilterSheetOpen(false)} title="Filter" variant="bottom">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <FilterChip active={isOpenPresetActive} tone="critical" label="Nur offene Maßnahmen" onClick={applyOpenPreset} />
              <FacetRows
                categories={categories}
                selectedCategories={selectedCategories} toggleCategory={toggleCategory}
                selectedStatuses={selectedStatuses} toggleStatus={toggleStatus}
                selectedPriorities={selectedPriorities} togglePriority={togglePriority}
              />
              {hasActiveFilters && (
                <button onClick={clearFilters} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 44, background: 'none', border: 'none', cursor: 'pointer', color: '#667085', fontSize: 13, fontFamily: FONT }}>
                  <X width={13} height={13} /> Filter zurücksetzen
                </button>
              )}
            </div>
          </Sheet>
        )}

        {/* ── Ergebnis ── */}
        {filtered.length === 0 ? (
          <Card padding={0}>
            <div style={{ padding: '56px 40px', textAlign: 'center' }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14, background: '#F2F6FE', color: BRAND,
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <ListChecks width={26} height={26} />
              </div>
              <h2 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#101623' }}>
                {isOpenPresetActive ? 'Keine offenen Maßnahmen' : 'Keine Kontrollen gefunden'}
              </h2>
              <p style={{ margin: 0, fontSize: 13.5, color: '#667085' }}>
                {isOpenPresetActive
                  ? 'Alle Maßnahmen sind erledigt oder nicht anwendbar.'
                  : 'Passen Sie die Filter oder die Suche an, um Ergebnisse zu sehen.'}
              </p>
            </div>
          </Card>
        ) : (
          categories.filter(c => grouped[c]?.length).map(cat => {
            const catRows = grouped[cat];
            return (
              <Card key={cat} padding={0} style={{ marginBottom: 16 }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #EDEFF3' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#101623' }}>{cat}</span>
                </div>
                {isMobile ? (
                  <div>
                    {catRows.map(({ question, record, originalStatus, progressStatus, priority }) => (
                      <ControlRowCard
                        key={question.id}
                        question={question} record={record} originalStatus={originalStatus}
                        progressStatus={progressStatus} priority={priority}
                        saving={savingIds.has(question.id)}
                        onOpen={() => navigate(`/audits/${auditId}/controls/${question.id}`)}
                        onProgressChange={(v) => handleProgressChange(question.id, v)}
                      />
                    ))}
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                      <tbody>
                        {catRows.map(({ question, record, originalStatus, progressStatus, priority }) => {
                          const original = originalStatus ? ORIGINAL_META[originalStatus] : null;
                          return (
                            <tr
                              key={question.id}
                              style={{ borderBottom: '1px solid #F2F3F5', cursor: 'pointer' }}
                              onClick={() => navigate(`/audits/${auditId}/controls/${question.id}`)}
                              onMouseEnter={e => e.currentTarget.style.background = '#FAFBFC'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <td style={{ padding: '12px 20px', width: 64, fontFamily: FONT_MONO, fontSize: 12, color: '#8A93A3', verticalAlign: 'top' }}>
                                {question.dinNr || '–'}
                              </td>
                              <td style={{ padding: '12px 8px', fontSize: 13, color: '#101623', verticalAlign: 'top' }}>
                                <div style={{ marginBottom: 4 }}>{question.text}</div>
                                {record?.notes && <div style={{ fontSize: 12, color: '#8A93A3', marginBottom: 4 }}>{record.notes}</div>}
                                {original && <Badge tone={original.tone}>{original.label}</Badge>}
                              </td>
                              <td style={{ padding: '12px 8px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                <Badge tone={priority.tone}>{priority.label}</Badge>
                              </td>
                              <td style={{ padding: '12px 20px', verticalAlign: 'top', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <ProgressSelect
                                    value={progressStatus}
                                    disabled={savingIds.has(question.id)}
                                    onChange={(v) => handleProgressChange(question.id, v)}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {openControl && (
        <ControlDrawer
          question={openControl}
          record={openRecord}
          originalStatus={deriveStatus(openControl, openRecord)}
          saving={savingIds.has(openControl.id)}
          onProgressChange={(v) => handleProgressChange(openControl.id, v)}
          onClose={() => navigate(`/audits/${auditId}/controls`)}
        />
      )}

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </Layout>
  );
};

export default ControlsPage;
